import { SearchMenuRender } from "./search-menu-render";
import { SearchMenuCategoryUtils } from "./category/search-menu-category-utils";
import { SearchMenuStrageUtils } from "./strage/search-menu-strage-utils";
import { phpAuthUser } from "../../core/php-data-access";
import { SearchMenuApi } from "./search-menu-api";
import { SearchMenuListener } from "./search-menu-listener";
import { SearchMenuForm } from "./search-menu-form";
import { getEventScope } from "../../core/event-scope/event-scope";
import { CommonUtils } from "../../util/common-utils";
import { ModalSearchSelectionIO } from "../../components/modal-search/modal-search-selection-io";
import { ChoicesControl } from "../../components/choices-control/choices-control-setup";
import { ChoicesControlUtils } from "../../components/choices-control/choices-control-utils";
import { enableModalTitlebarDrag } from "../../util/modal-titlebar-drag";
import { ModalNestedPosition } from "../../util/modal-nested-position";
import { spinner } from "../../util/spinner/spinner";
import { Roles } from "../../constants/roles";
import type { SearchMenuRecord } from "./search-menu-api";
import type { SearchMenuPersistedState } from "./strage/search-menu-strage-utils";

type SearchMenuData = Awaited<ReturnType<SearchMenuApi["getSearchMenu"]>>;
type SearchMenuInputElement = HTMLInputElement;
type BodyOverflowRestoreState = {
    overflow: string;
};
type SearchMenuRenderExtraOptions = {
    persistedState?: SearchMenuPersistedState | null;
};

let activeSearchMenu: SearchMenuSetup | null = null;
const searchMenuForm = new SearchMenuForm();
let postProductPickerInstanceSeq = 0;

function ensureGlobalSearchMenuListeners() {
    const scope = getEventScope(document, "search-menu");

    // If Choices handles selection on mousedown/pointerdown, it can remove the dropdown
    // before the subsequent click event. In that case, the click may land on the overlay
    // behind the dropdown and accidentally close the menu.
    // Track the pointerdown source to suppress the next outside/overlay close.
    scope.once("global-pointerdown-choices", () => {
        scope.on(
            "pointerdown",
            (e) => {
                if (!activeSearchMenu) return;

                const target = e.target;
                if (!(target instanceof Element)) return;

                const path = typeof e.composedPath === "function" ? e.composedPath() : [];
                const startedInChoicesDropdown =
                    !!searchMenuForm.getClosestChoicesDropdown(target) ||
                    path.some((node) => node instanceof Element && node.classList.contains("choices__list--dropdown"));
                if (!startedInChoicesDropdown) return;

                const menu = activeSearchMenu;
                // Suppress the next outside-click close (document click handler)
                menu._suppressOutsideClickOnce = true;
                // Also suppress overlay close for a short window.
                menu._ignoreOutsideCloseUntil = Date.now() + 600;
            },
            { capture: true },
        );
    });

    scope.once("global-click-outside", () => {
        scope.on("click", (e) => {
            if (!activeSearchMenu) return;

            const target = e.target;
            if (!(target instanceof Element)) return;

            // Choices can synchronously re-render/remove dropdown items on click,
            // which makes `target.closest(...)` unreliable by the time the event
            // bubbles to document. Use composedPath() for a stable check.
            const path = typeof e.composedPath === "function" ? e.composedPath() : [];
            const clickedInChoicesDropdown =
                !!searchMenuForm.getClosestChoicesDropdown(target) ||
                path.some((node) => node instanceof Element && node.classList.contains("choices__list--dropdown"));
            if (clickedInChoicesDropdown) return;

            // When the independent category modal is open, ignore clicks inside it.
            // Otherwise, clicking (e.g. the parent ✁Ebutton) would close the search menu.
            const categoryModal = searchMenuForm.getClosestCategoryModal(target);
            if (categoryModal) return;

            const brandModal = searchMenuForm.getClosestBrandModal(target);
            if (brandModal) return;

            const categoryReviewEditor = searchMenuForm.getClosestCategoryReviewEditor(target);
            if (categoryReviewEditor) return;

            const floatingChat = searchMenuForm.getClosestFloatingChat(target);
            if (floatingChat) return;

            const menu = activeSearchMenu;

            if (menu?._ignoreOutsideCloseUntil && Date.now() < menu._ignoreOutsideCloseUntil) {
                return;
            }

            // If the menu was opened by the same user click that triggered this document click,
            // suppress closing once. This prevents "open then immediately close" glitches.
            if (menu?._suppressOutsideClickOnce) {
                menu._suppressOutsideClickOnce = false;
                return;
            }
            if (!menu.inputElement || !menu.menuContainer) return;
            if (!menu.inputElement.contains(target) && !menu.menuContainer.contains(target)) {
                menu.hideMenu();
            }
        });
    });

    scope.once("global-escape", () => {
        scope.on("keydown", (e) => {
            if (!(e instanceof KeyboardEvent)) return;
            if (e.key !== "Escape") return;
            if (!activeSearchMenu) return;

            const menu = activeSearchMenu;
            // Close any open Choices dropdowns first.
            // SearchMenuRender no longer tracks instances; they live on each select.
            try {
                const root = menu.menuContainer;
                if (root instanceof HTMLElement) {
                    const selects = searchMenuForm.getChoicesSelects(root);
                    let closedAny = false;

                    selects.forEach((sel) => {
                        const inst = ChoicesControlUtils.asInternalSelect(sel).__choicesInstance ?? null;
                        const isActive = inst?.dropdown?.isActive === true;

                        if (isActive && typeof inst?.hideDropdown === "function") {
                            inst.hideDropdown();
                            closedAny = true;
                        }
                    });

                    if (closedAny) return;
                }
            } catch (_e) {
                // ignore
            }

            menu.hideMenu();
        });
    });

    scope.once("global-enter-product-picker", () => {
        scope.on(
            "keydown",
            (e) => {
                if (!(e instanceof KeyboardEvent)) return;
                if (e.key !== "Enter") return;
                if (!activeSearchMenu || activeSearchMenu.mode !== "post-product-picker") return;

                const target = e.target;
                if (!(target instanceof HTMLInputElement)) return;

                const inputType = String(target.type || "text").toLowerCase();
                if (["checkbox", "radio", "button", "submit", "hidden"].includes(inputType)) {
                    return;
                }

                const menu = activeSearchMenu;
                const withinPicker =
                    !!menu.inputElement?.contains(target) ||
                    !!menu.menuContainer?.contains(target) ||
                    target === menu.inputElement;
                if (!withinPicker) return;

                const applyBtn = menu.menuContainer
                    ? searchMenuForm.getSearchMenuConfirmButton(menu.menuContainer)
                    : null;
                if (!(applyBtn instanceof HTMLButtonElement) || applyBtn.disabled) return;

                e.preventDefault();
                e.stopPropagation();
                applyBtn.click();
            },
            { capture: true },
        );
    });
}

/**
 * Search Menu Main Controller
 * Coordinates search menu functionality
 */
export class SearchMenuSetup {
    public inputElement: SearchMenuInputElement;
    public menuContainer: HTMLElement | null;
    public overlayElement: HTMLElement | null;
    public api: SearchMenuApi;
    public Render: SearchMenuRender;
    public categoryHandler: SearchMenuCategoryUtils;
    public isMenuVisible: boolean;
    public menuData: SearchMenuData | null;
    public mode: string;
    public persist: SearchMenuStrageUtils;
    private readonly form: SearchMenuForm;
    private _modalUiBound: boolean;
    public _suppressOutsideClickOnce: boolean;
    public _ignoreOutsideCloseUntil: number;
    private _bodyOverflowRestore: BodyOverflowRestoreState | null;
    private _urlSelectionsHydrated: boolean;
    private _postProductPickerOwnerId: string | null;

    public constructor(inputElement: SearchMenuInputElement) {
        this.inputElement = inputElement;
        this.menuContainer = null;
        this.overlayElement = null;
        this.api = new SearchMenuApi();
        this.Render = new SearchMenuRender();
        this.categoryHandler = new SearchMenuCategoryUtils();
        this.isMenuVisible = false;
        this.menuData = null;
        this.mode = this.inputElement?.dataset?.searchMenuMode || "default";
        this.form = new SearchMenuForm();

        this._modalUiBound = false;

        this._suppressOutsideClickOnce = false;

        // Timestamp (ms) until which outside/overlay clicks should be ignored.
        // Used for dropdown interactions (Choices) where the subsequent click can land
        // on the overlay after the dropdown removes itself.
        this._ignoreOutsideCloseUntil = 0;

        this._bodyOverflowRestore = null;

        this._urlSelectionsHydrated = false;

        this._postProductPickerOwnerId = null;

        this.persist = new SearchMenuStrageUtils({ namespace: "products", version: 1 });

        this.init();
    }

    public hasAnySearchFiltersInUrl() {
        try {
            const params = new URLSearchParams(globalThis?.location?.search || "");
            const keys = [
                "search",
                "category_id",
                "product_category_id",
                "parent_category_id",
                "product_parent_category_id",
                "brand_id",
                "product_id",
                "user_id",
                "user",
                "content_search",
                "content_search_fields",
                "advanced_flags",
                "new_only",
                "recommend_only",
                "ranking_only",
                "reviewed_only",
                "scored_only",
                "exclude_text_only",
                "sort",
                "order",
                "rating",
                "updated_by",
                "status",
                "updated_at_from",
                "updated_at_to",
            ];

            return keys.some((k) => {
                const v = params.getAll(k).join(",") || params.get(k) || "";
                return String(v || "").trim().length > 0;
            });
        } catch (_e) {
            return false;
        }
    }

    public bindPersistToggleUi() {
        const input = this.menuContainer ? this.form.getSearchMenuPersistToggle(this.menuContainer) : null;
        if (!(input instanceof HTMLInputElement)) return;

        // Reflect persisted enabled state.
        input.checked = this.persist.isEnabled();

        // Avoid double-binding because search-menu DOM persists.
        if (input.dataset.persistBound === "1") return;
        input.dataset.persistBound = "1";

        SearchMenuListener.bindChange(input, () => {
            const enabled = !!input.checked;
            this.persist.setEnabled(enabled);
        });
    }

    public onApplyDefaultSearch() {
        if (this.persist.isEnabled()) {
            try {
                const snapshot = this.Render.getPersistedStateSnapshot?.();
                if (snapshot && typeof snapshot === "object") {
                    this.persist.saveState(snapshot);
                }
            } catch (_e) {
                // ignore
            }
        }

        this.Render.executeSearch();
    }

    private getMenuDisplayValue(): "flex" {
        return "flex";
    }

    public onClearDefaultSearch() {
        this.Render.clearAllSelections();
        this.Render.renderResultsEmpty();

        // Clear saved filters as well.
        this.persist.clearState();
    }

    /**
     * Hydrate initial category/brand selections from current URL query params.
     * This ensures chips reflect existing filters on first open.
     */
    public hydrateSelectionsFromUrl() {
        if (this._urlSelectionsHydrated) return;
        this._urlSelectionsHydrated = true;

        try {
            const search = globalThis?.location?.search || "";
            const params = new URLSearchParams(String(search));

            // Support both comma-separated and repeated query params.
            const rawCategory =
                params.getAll("product_category_id").join(",") ||
                params.get("product_category_id") ||
                params.getAll("category_id").join(",") ||
                params.get("category_id") ||
                "";
            const rawParentCategory =
                params.getAll("product_parent_category_id").join(",") ||
                params.get("product_parent_category_id") ||
                params.getAll("parent_category_id").join(",") ||
                params.get("parent_category_id") ||
                "";
            const rawBrand = params.getAll("brand_id").join(",") || params.get("brand_id") || "";
            const rawUser =
                params.getAll("user_id").join(",") ||
                params.get("user_id") ||
                params.getAll("user").join(",") ||
                params.get("user") ||
                "";

            const childCategories = ModalSearchSelectionIO.parseIdsFromValue(rawCategory, { mode: "multi" });
            const parentCategories = ModalSearchSelectionIO.parseIdsFromValue(rawParentCategory, { mode: "multi" });
            const categories = Array.from(new Set([...parentCategories, ...childCategories]));
            const brands = ModalSearchSelectionIO.parseIdsFromValue(rawBrand, { mode: "multi" });
            const users = ModalSearchSelectionIO.parseIdsFromValue(rawUser, { mode: "multi" });

            this.Render.setInitialPickerSelections({ categories, brands, users });
        } catch (e) {
            console.warn("[SearchMenuSetup] Failed to hydrate selections from URL", e);
        }
    }

    /**
     * Initialize search menu
     */
    public init() {
        if (!this.inputElement) {
            console.error("[SearchMenuSetup] Input element not found");
            return;
        }

        // Create menu container
        this.createMenuContainer();

        // Attach event listeners
        this.attachEventListeners();

        // Preload menu data (non-blocking)
        this.loadMenuData().catch((error) => {
            console.error("[SearchMenuSetup] Failed to preload menu data:", error);
        });

        // Initialize selection state from current URL (first load).
        this.hydrateSelectionsFromUrl();
    }

    /**
     * Create menu container element
     */
    public createMenuContainer() {
        const parent = this.inputElement?.parentElement;
        if (!parent) return;

        // Default behavior: render as a centered modal (overlay + fixed dialog).
        // This matches the new spec UI (Category Search modal) and avoids layout clipping.
        if (this.mode !== "post-product-picker") {
            // Prefer Blade-prepared DOM (spec: UI is prepared in Blade, hidden by default)
            const existingOverlay = this.form.getSearchMenuOverlay(parent);
            const existingContainer = this.form.getSearchMenuContainer(parent);

            if (existingOverlay instanceof HTMLElement && existingContainer instanceof HTMLElement) {
                if (existingOverlay.parentElement !== document.body) {
                    document.body.appendChild(existingOverlay);
                }

                // Click outside to close
                if (!this._modalUiBound) {
                    SearchMenuListener.bindClick(existingOverlay, (e) => {
                        if (this._ignoreOutsideCloseUntil && Date.now() < this._ignoreOutsideCloseUntil) return;
                        if (e.target !== existingOverlay) return;
                        this.hideMenu();
                    });
                    // Prevent inner clicks from bubbling to overlay
                    SearchMenuListener.bindClick(existingContainer, (e) => {
                        e.stopPropagation();
                    });
                    this._modalUiBound = true;
                }

                this.overlayElement = existingOverlay;
                this.menuContainer = existingContainer;
                return;
            }

            const modalShell = SearchMenuRender.renderModalOverlayShell();
            if (!modalShell) return;
            const { overlay, container } = modalShell;

            // Click outside to close
            SearchMenuListener.bindClick(overlay, (e) => {
                if (this._ignoreOutsideCloseUntil && Date.now() < this._ignoreOutsideCloseUntil) return;
                if (e.target !== overlay) return;
                this.hideMenu();
            });

            // Prevent inner clicks from bubbling to overlay
            SearchMenuListener.bindClick(container, (e) => {
                e.stopPropagation();
            });

            this.overlayElement = overlay;
            this.menuContainer = container;
            return;
        }

        // post-product-picker: use Blade-prepared DOM, but still show it as a centered modal overlay.
        const existing = this.resolvePostProductPickerContainer();

        if (!(existing instanceof HTMLElement)) {
            console.error(
                "[SearchMenuSetup] Blade-prepared container not found for post-product-picker. Skipping init.",
            );
            return;
        }

        const overlay = SearchMenuRender.renderModalOverlayWithExisting(existing);
        if (!(overlay instanceof HTMLDivElement)) return;

        // Click outside to close
        SearchMenuListener.bindClick(overlay, (e) => {
            if (this._ignoreOutsideCloseUntil && Date.now() < this._ignoreOutsideCloseUntil) return;
            if (e.target !== overlay) return;
            this.hideMenu();
        });

        // Prevent inner clicks from bubbling to overlay
        SearchMenuListener.bindClick(existing, (e) => {
            e.stopPropagation();
        });

        this.overlayElement = overlay;
        this.menuContainer = existing;
        return;
    }

    private resolvePostProductPickerContainer(): HTMLElement | null {
        if (this.mode !== "post-product-picker") {
            return null;
        }

        if (!this._postProductPickerOwnerId) {
            postProductPickerInstanceSeq += 1;
            this._postProductPickerOwnerId = `post-product-picker-${postProductPickerInstanceSeq}`;
        }

        const ownerId = this._postProductPickerOwnerId;
        const containers = this.form.getSearchMenuPostProductPickerContainers(document);

        const owned = containers.find((container) => container.dataset.searchMenuOwnerId === ownerId);
        if (owned instanceof HTMLElement) {
            return owned;
        }

        const unclaimed = containers.find((container) => !container.dataset.searchMenuOwnerId);
        if (unclaimed instanceof HTMLElement) {
            unclaimed.dataset.searchMenuOwnerId = ownerId;
            return unclaimed;
        }

        return containers[0] instanceof HTMLElement ? containers[0] : null;
    }

    /**
     * Load menu data from API (using shared cache)
     * @returns {Promise<void>}
     */
    public async loadMenuData() {
        // Check if data is already cached
        const cachedData = SearchMenuSetup.getSharedMenuData();
        if (cachedData) {
            this.menuData = await cachedData;
            return;
        }

        try {
            // Create a shared promise to prevent duplicate requests
            const promise = this.api.getSearchMenu();
            SearchMenuSetup.setSharedMenuDataPromise(promise);

            this.menuData = await promise;
            SearchMenuSetup.setSharedMenuData(this.menuData);
        } catch (error) {
            console.error("[SearchMenuSetup] Failed to load menu data:", error);
            this.menuData = null;
            SearchMenuSetup.setSharedMenuDataPromise(null);
        }
    }

    /**
     * Attach event listeners
     */
    public attachEventListeners() {
        ensureGlobalSearchMenuListeners();

        // Focus event - show menu
        SearchMenuListener.bindFocus(this.inputElement, () => {
            if (this.inputElement.dataset.suppressSearchMenuOpenOnce === "1") {
                delete this.inputElement.dataset.suppressSearchMenuOpenOnce;
                return;
            }

            const openOnFocus = this.inputElement.dataset.openSearchMenuOnNextFocus === "1";
            delete this.inputElement.dataset.openSearchMenuOnNextFocus;

            if (this.mode === "navbar" && !openOnFocus) {
                return;
            }

            this.showMenu();
        });

        // Input event
        // - default: hide menu when typing (suggest is dropdown only)
        // - post-product-picker: keep menu open and let user trigger API via search button
        SearchMenuListener.bindInput(this.inputElement, () => {
            const value = String(this.inputElement.value || "").trim();

            if (this.mode === "post-product-picker") {
                // Keep menu open to show results area and selections.
                if (!this.isMenuVisible) this.showMenu();
                return;
            }

            if (value.length > 0) {
                this.hideMenu();
            } else {
                this.showMenu();
            }
        });

        // Click outside / Escape are handled globally via EventScope + activeSearchMenu routing
    }

    /**
     * Show search menu
     */
    public showMenu() {
        activeSearchMenu = this;

        if (!this.menuContainer) {
            console.error("[SearchMenuSetup] Menu container not initialized; cannot show menu.");
            return;
        }

        // Avoid immediate close caused by the same click that focused the input.
        this._suppressOutsideClickOnce = true;

        // In modal overlay mode, lock body scroll while the menu is open.
        if (this.overlayElement instanceof HTMLElement) {
            if (!this._bodyOverflowRestore) {
                this._bodyOverflowRestore = {
                    overflow: document.body.style.overflow,
                };
            }
            document.body.style.overflow = "hidden";

            if (this.mode === "post-product-picker") {
                ModalNestedPosition.openOverlayModalAsNested({
                    overlayEl: this.overlayElement,
                    modalEl: this.menuContainer,
                    parentModalSelector: `[data-role="${Roles.ELEMENT_MODAL}"].element-modal--review-create, [data-role="${Roles.ELEMENT_MODAL}"].element-modal--post-create, [data-role="${Roles.ELEMENT_MODAL}"], [data-role="${Roles.CATEGORY_REVIEW_EDITOR_MODAL}"].visible, [data-role="${Roles.SEARCH_MENU_POST_PRODUCT_PICKER_CONTAINER}"].visible, [data-role="${Roles.SEARCH_CATEGORY_MODAL}"].visible, [data-role="${Roles.SEARCH_BRAND_MODAL}"].visible, [data-role="${Roles.SEARCH_USER_MODAL}"].visible`,
                    overlayDisplayValue: "block",
                    modalDisplayValue: this.getMenuDisplayValue(),
                    deferVisibleClass: true,
                    raiseZIndexAboveParent: true,
                });
            } else {
                ModalNestedPosition.openOverlayModal({
                    overlayEl: this.overlayElement,
                    modalEl: this.menuContainer,
                    overlayDisplayValue: "block",
                    modalDisplayValue: this.getMenuDisplayValue(),
                    deferVisibleClass: true,
                });
                // On mobile the search menu is always the first nested level (depth=1).
                // Nested modals opened from within it will inherit depth+1.
                if (CommonUtils.isMobileViewport() && this.menuContainer instanceof HTMLElement) {
                    this.menuContainer.dataset.modalDepth = "1";
                    this.menuContainer.classList.add("is-nested-modal");
                }
            }
            this.isMenuVisible = true;
        } else {
            ModalNestedPosition.openModal({
                modalEl: this.menuContainer,
                displayValue: this.getMenuDisplayValue(),
                deferVisibleClass: true,
            });
            this.isMenuVisible = true;
        }

        // Keep toggle UI in sync with persisted enabled state.
        this.bindPersistToggleUi();

        const urlHasFilters = this.hasAnySearchFiltersInUrl();
        const persistedState = this.persist.isEnabled() ? this.persist.loadState() : null;
        const shouldApplyPersistedState = this.mode !== "post-product-picker" && !urlHasFilters && !!persistedState;

        // Check if menu is already initialized
        if (this.menuContainer?.dataset?.searchMenuInitialized === "1") {
            // Menu DOM persists between opens.
            // IMPORTANT: don't blindly sync from URL, because when the URL does not include
            // user filters it would clear in-memory selections (including persisted state).
            if (shouldApplyPersistedState) {
                this.Render.applyPersistedState?.(persistedState);
            } else {
                const hasUrlUserFilters = (() => {
                    try {
                        const p = new URLSearchParams(globalThis?.location?.search || "");
                        return !!(
                            p.getAll("user_id").join(",") ||
                            p.get("user_id") ||
                            p.getAll("user").join(",") ||
                            p.get("user") ||
                            p.getAll("updated_by").join(",") ||
                            p.get("updated_by")
                        );
                    } catch (_e) {
                        return false;
                    }
                })();

                if (this.mode !== "post-product-picker" && hasUrlUserFilters) {
                    // Re-hydrate user-based pickers from the current URL so the UI reflects
                    // the active query params.
                    this.Render.syncUserPickersFromUrl?.();
                }
            }

            this.resetPostProductPickerState();

            // Resolve chip labels (id -> display name) via API. Render never calls API directly.
            try {
                const ids = this.Render.getUserIdsToResolve?.() || [];
                if (Array.isArray(ids) && ids.length > 0) {
                    this.api
                        .resolveUserLabelsByIds(ids)
                        .then((labelMap) => {
                            this.Render.applyUserLabels?.(labelMap);
                        })
                        .catch((_e) => {
                            // ignore
                        });
                }
            } catch (_e) {
                // ignore
            }

            this.Render.syncPickersFromHidden?.();
            this.Render.applyContentSearchDefaultsIfInactive();
            return;
        }

        // If data is not loaded, show spinner
        if (!this.menuData) {
            this.showLoadingSpinner();

            // Load data and render when ready
            this.loadMenuData().then(() => {
                if (this.menuData) {
                    this.Render.render(
                        this.menuContainer,
                        this.menuData,
                        () => this.hideMenu(),
                        this.getRenderOptions({ persistedState: shouldApplyPersistedState ? persistedState : null }),
                    );

                    this.bindPersistToggleUi();
                    if (shouldApplyPersistedState) {
                        this.Render.applyPersistedState?.(persistedState);
                    }
                    this.resetPostProductPickerState();

                    // Resolve chip labels (id -> display name) via API (Render is UI-only).
                    try {
                        const ids = this.Render.getUserIdsToResolve?.() || [];
                        if (Array.isArray(ids) && ids.length > 0) {
                            this.api
                                .resolveUserLabelsByIds(ids)
                                .then((labelMap) => {
                                    this.Render.applyUserLabels?.(labelMap);
                                })
                                .catch((_e) => {
                                    // ignore
                                });
                        }
                    } catch (_e) {
                        // ignore
                    }

                    enableModalTitlebarDrag(this.menuContainer, { isEnabled: () => this.isMenuVisible });
                } else {
                    this.showErrorMessage();
                }
            });
        } else {
            // Data is already loaded, render immediately
            this.Render.render(
                this.menuContainer,
                this.menuData,
                () => this.hideMenu(),
                this.getRenderOptions({ persistedState: shouldApplyPersistedState ? persistedState : null }),
            );

            this.bindPersistToggleUi();
            if (shouldApplyPersistedState) {
                this.Render.applyPersistedState?.(persistedState);
            }
            this.resetPostProductPickerState();

            // Resolve chip labels (id -> display name) via API (Render is UI-only).
            try {
                const ids = this.Render.getUserIdsToResolve?.() || [];
                if (Array.isArray(ids) && ids.length > 0) {
                    this.api
                        .resolveUserLabelsByIds(ids)
                        .then((labelMap) => {
                            this.Render.applyUserLabels?.(labelMap);
                        })
                        .catch((_e) => {
                            // ignore
                        });
                }
            } catch (_e) {
                // ignore
            }

            enableModalTitlebarDrag(this.menuContainer, { isEnabled: () => this.isMenuVisible });
        }
    }

    public getRenderOptions(extra?: SearchMenuRenderExtraOptions) {
        const extraOptions = extra ?? {};

        if (this.mode !== "post-product-picker") {
            return {
                mode: this.mode,
                persistedState: extraOptions.persistedState || null,
                onApply: () => this.onApplyDefaultSearch(),
                onClear: () => this.onClearDefaultSearch(),
            };
        }

        return {
            mode: this.mode,
            getQuery: () => this.inputElement?.value || "",
            onApply: ({ selections, query }) => this.onApplyProductPicker(selections, query),
            onSetProduct: (product) => this.onSetProduct(product),
        };
    }

    private resetPostProductPickerState(): void {
        if (this.mode !== "post-product-picker") {
            return;
        }

        this.Render.clearAllSelections?.();
        this.Render.renderResultsEmpty?.();
    }

    /**
     * Show loading spinner in menu
     */
    public showLoadingSpinner() {
        this.Render.renderLoading(this.menuContainer);
    }

    /**
     * Show error message in menu
     */
    public showErrorMessage() {
        this.Render.renderError(this.menuContainer);
    }

    /**
     * Hide search menu
     */
    public hideMenu() {
        if (activeSearchMenu === this) {
            activeSearchMenu = null;
        }

        if (this.overlayElement instanceof HTMLElement) {
            if (this.menuContainer instanceof HTMLElement) {
                this.menuContainer.classList.remove("is-nested-modal");
                delete this.menuContainer.dataset.modalDepth;
            }
            ModalNestedPosition.closeOverlayModal({
                overlayEl: this.overlayElement,
                modalEl: this.menuContainer,
                hideDelayMs: 200,
            });
        } else {
            ModalNestedPosition.closeModal({
                modalEl: this.menuContainer,
                hideDelayMs: 200,
            });
        }

        this.isMenuVisible = false;

        if (this._bodyOverflowRestore) {
            document.body.style.overflow = this._bodyOverflowRestore.overflow;
            this._bodyOverflowRestore = null;
        }
    }

    /**
     * Handle category selection
     * @param {Object} selection
     */
    public onCategorySelect(selection) {
        this.categoryHandler.updateSelection(selection);

        // Default behavior: auto navigate
        if (this.mode !== "post-product-picker") {
            const query = this.inputElement.value.trim();
            this.categoryHandler.executeSearch(query);
        }
    }

    /**
     * Product picker mode: run API on search button click and render results inside menu.
     * @param {Object} selections
     * @param {string} query
     */
    public async onApplyProductPicker(selections, query) {
        const applyButton = this.menuContainer ? this.form.getSearchMenuConfirmButton(this.menuContainer) : null;
        const startedAt = Date.now();

        try {
            if (this.persist.isEnabled()) {
                try {
                    const snapshot = this.Render.getPersistedStateSnapshot?.();
                    if (snapshot && typeof snapshot === "object") {
                        this.persist.saveState(snapshot);
                    }
                } catch (_e) {
                    // ignore
                }
            }

            if (applyButton instanceof HTMLButtonElement) {
                spinner.showButtonLoader(applyButton, { translateKey: "spinner_proccesing" });
                await new Promise<void>((resolve) => {
                    globalThis.requestAnimationFrame(() => resolve());
                });
            }

            this.Render.setApplyDisabled(true);
            // Loading state now lives in the button itself (spinner.showButtonLoader above),
            // matching post-edit-save's pattern — no separate "Loading..." panel needed.

            const params = new URLSearchParams();

            const contentSearchInput = this.menuContainer
                ? this.form.getNamedInput(this.menuContainer, "content_search")
                : null;
            const contentSearch =
                contentSearchInput instanceof HTMLInputElement ? String(contentSearchInput.value || "").trim() : "";

            const kwParts = [];
            const q = String(query || "").trim();
            if (q) kwParts.push(q);

            const keywords = Array.isArray(selections?.keywords) ? selections.keywords : [];
            if (keywords.length > 0) {
                kwParts.push(
                    keywords
                        .map((v) => String(v || "").trim())
                        .filter(Boolean)
                        .join(" "),
                );
            }

            // post-product-picker reuses this same "content_search"-named input for its
            // single "Keyword" field (admin/content-search mode's own three-field layout
            // — category/brand/content-search — doesn't apply there), but a picker user
            // expects it to match the product name like any other product search, not be
            // scoped to content_search's narrow description/how_to_use/ingredients fields
            // (ProductService::applyAdvancedSearchFilters()). Route it into the general
            // `search` param instead for this mode, so typing a product name actually
            // finds it.
            if (this.mode === "post-product-picker" && contentSearch) {
                kwParts.push(contentSearch);
            }

            const search = kwParts
                .map((v) => v.trim())
                .filter(Boolean)
                .join(" ")
                .trim();
            if (search) {
                params.set("search", search);
            }

            if (this.mode !== "post-product-picker" && contentSearch) {
                params.set("content_search", contentSearch);

                const fieldsHidden = this.menuContainer
                    ? this.form.getNamedHiddenInput(this.menuContainer, "content_search_fields")
                    : null;
                if (fieldsHidden instanceof HTMLInputElement) {
                    const fields = ChoicesControl.normalizeStringList(fieldsHidden.value);
                    if (fields.length > 0) {
                        params.set("content_search_fields", fields.join(","));
                    }
                }
            }

            const categories = Array.isArray(selections?.categories) ? selections.categories : [];
            const normalizedCategoryIds = categories.map((v) => String(v || "").trim()).filter(Boolean);

            const parentCategoryIds = [];
            const childCategoryIds = [];

            normalizedCategoryIds.forEach((id) => {
                const n = Number(id);
                if (Number.isInteger(n) && n > 0 && n % 100 === 0) {
                    parentCategoryIds.push(String(n));
                    return;
                }
                childCategoryIds.push(id);
            });

            parentCategoryIds.forEach((id) => params.append("parent_category_id", id));
            childCategoryIds.forEach((id) => params.append("category_id", id));

            const brands = Array.isArray(selections?.brands) ? selections.brands : [];
            const normalizedBrandIds = brands.map((v) => String(v || "").trim()).filter(Boolean);
            normalizedBrandIds.forEach((id) => params.append("brand_id", id));

            const products = Array.isArray(selections?.products) ? selections.products : [];
            products
                .map((v) => String(v || "").trim())
                .filter(Boolean)
                .forEach((id) => params.append("product_id", id));

            params.set("page", "1");
            params.set("per_page", "10");

            const json = await this.api.searchProductsBySelections(params);

            const items = (Array.isArray(json?.data) ? json.data : []) as unknown as SearchMenuRecord[];
            if (!json || !Array.isArray(json.data)) {
                this.Render.renderResultsError("Unexpected error");
                return;
            }

            // ISS-02-209: when exactly one brand is selected (ambiguous
            // otherwise), always offer that brand's own placeholder product as
            // the last result — not just when there are zero real matches.
            // The exact product the user wants may simply not be in this list.
            let brandPlaceholder: SearchMenuRecord | null = null;
            if (this.mode === "post-product-picker" && normalizedBrandIds.length === 1) {
                brandPlaceholder = await this.api.fetchBrandPlaceholderProduct(normalizedBrandIds[0]);
            }

            this.Render.renderProductResults(items, brandPlaceholder);
        } catch (error) {
            console.error("[SearchMenuSetup] Product picker search failed:", error);
            const errorMessage = error instanceof Error ? error.message : String(error || "Error");
            this.Render.renderResultsError(errorMessage);
        } finally {
            if (applyButton instanceof HTMLButtonElement) {
                const elapsed = Date.now() - startedAt;
                const remaining = 250 - elapsed;
                if (remaining > 0) {
                    await new Promise<void>((resolve) => globalThis.setTimeout(resolve, remaining));
                }
                spinner.hideButtonLoader(applyButton);
            }
            this.Render.setApplyDisabled(false);
        }
    }

    /**
     * Product picker mode: dispatch selection event for the owning input.
     * @param {SearchMenuRecord} product
     */
    public onSetProduct(product: SearchMenuRecord) {
        const ev = new CustomEvent("search-menu:product-selected", {
            bubbles: true,
            detail: {
                product,
                input: this.inputElement,
            },
        });

        this.inputElement.dispatchEvent(ev);
        this.hideMenu();
    }

    /**
     * Handle item click (brand or product)
     * @param {string} type - 'brand' or 'product'
     * @param {string} id - Item ID
     */
    public async onItemClick(type, id) {
        // Get current user ID (0 for guests)
        const userId = phpAuthUser().user_id || 0;

        // Save search history
        try {
            await this.api.saveSearchHistory(userId, id, type, "products");
        } catch (error) {
            console.error("Failed to save search history:", error);
        }

        // Navigate to page
        let url;
        switch (type) {
            case "brand":
                url = `/brands/${id}`;
                break;
            case "product":
                url = `/products/${id}`;
                break;
            default:
                console.warn("[SearchMenuSetup] Unsupported item type:", type);
                return;
        }

        document.location.href = CommonUtils.resolveUrl(url);
    }

    /**
     * Clear category selection
     */
    public clearSelection() {
        this.categoryHandler.clearSelection();
        this.Render.clearAllSelections();
    }

    /**
     * Destroy search menu
     */
    public destroy() {
        this.hideMenu();
        this.Render.destroy();

        if (this.menuContainer && this.menuContainer.parentElement) {
            this.menuContainer.parentElement.removeChild(this.menuContainer);
        }

        this.menuContainer = null;
        this.menuData = null;
    }

    /**
     * Initialize search menu for all opt-in search inputs.
     * @param {Document|HTMLElement} [rootEl]
     */
    public static init(rootEl) {
        return SearchMenuSetup.initAll(rootEl);
    }

    /**
     * Initialize search menu for all opt-in search inputs.
     * @param {Document|HTMLElement} [rootEl]
     */
    public static initAll(rootEl) {
        const root = rootEl ?? document;
        const form = new SearchMenuForm();
        const searchInputs = form.getSearchMenuInputs(root);
        const instances = [];

        searchInputs.forEach((input) => {
            try {
                if (!(input instanceof HTMLInputElement)) return;

                // Idempotency guard: this init may run multiple times (e.g. DOM replaced by modal/partial).
                if (input.dataset.searchMenuInitialized === "1") return;
                input.dataset.searchMenuInitialized = "1";

                const instance = new SearchMenuSetup(input);
                instances.push(instance);
            } catch (error) {
                console.error("[SearchMenuSetup] Failed to initialize for input:", input, error);
            }
        });

        // Initialize search forms
        SearchMenuListener.initializeSearchForms(root);

        return instances;
    }

    /**
     * Get shared menu data (cached)
     * @returns {Promise<Object|null>|null}
     */
    public static getSharedMenuData() {
        if (sharedMenuDataCache) {
            return Promise.resolve(sharedMenuDataCache);
        }
        if (sharedMenuDataPromise) {
            return sharedMenuDataPromise;
        }
        return null;
    }

    /**
     * Set shared menu data cache
     * @param {Object} data - Menu data
     */
    public static setSharedMenuData(data) {
        sharedMenuDataCache = data;
        sharedMenuDataPromise = null;
    }

    /**
     * Set shared menu data promise
     * @param {Promise|null} promise - Promise for menu data
     */
    public static setSharedMenuDataPromise(promise) {
        sharedMenuDataPromise = promise;
    }
}

// Shared cache for menu data across all instances
let sharedMenuDataCache: SearchMenuData | null = null;
let sharedMenuDataPromise: Promise<SearchMenuData> | null = null;
